/** Comptabilité API layer (spec docs/spec-comptabilite.md). */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import { apiDelete, apiGet, apiPatch, apiPost, type QueryParams } from "@/lib/api-client";

// ── Types (raw API shapes; decimals may arrive as strings) ──────────────────

export type Money = number | string;

export interface TxRow {
  id: string;
  reference: string;
  title: string;
  direction: "IN" | "OUT";
  source?: string | null;
  category?: string | null;
  supplierName?: string | null;
  amount: Money;
  currency: string;
  status:
    | "DRAFT"
    | "PENDING_APPROVAL"
    | "APPROVED"
    | "PAID"
    | "REJECTED"
    | "TO_CONFIRM"
    | "CANCELLED";
  requiresApproval: boolean;
  sourceType?: string | null;
  date: string;
  notes?: string | null;
  rejectReason?: string | null;
  client?: { id: string; name: string } | null;
  project?: { id: string; name: string } | null;
  invoice?: { id: string; number: string } | null;
  account?: { id: string; name: string; currency: string } | null;
  correctionOf?: { id: string; reference: string } | null;
}

export interface RecurringRow {
  id: string;
  name: string;
  recurringType: string;
  direction: "IN" | "OUT";
  category?: string | null;
  amount: Money;
  currency: string;
  frequency: "WEEKLY" | "MONTHLY" | "QUARTERLY" | "SEMIANNUAL" | "ANNUAL";
  startDate: string;
  nextExecutionAt: string;
  endDate?: string | null;
  autoCreate: boolean;
  requiresValidation: boolean;
  status: "ACTIVE" | "SUSPENDED" | "STOPPED";
  lastExecutedAt?: string | null;
  supplierName?: string | null;
  deletedAt?: string | null;
  account?: { id: string; name: string; currency: string } | null;
  client?: { id: string; name: string } | null;
}

export interface DebtRow {
  id: string;
  reference?: string | null;
  creditorName: string;
  reason: string;
  category?: string | null;
  totalAmount: Money;
  paidAmount: Money;
  currency: string;
  issueDate: string;
  dueDate: string;
  status: "PENDING" | "PARTIAL" | "SETTLED" | "OVERDUE" | "DISPUTED" | "CANCELLED";
  notes?: string | null;
  project?: { id: string; name: string } | null;
  account?: { id: string; name: string } | null;
  payments: Array<{
    id: string;
    amount: Money;
    date: string;
    note?: string | null;
    account?: { id: string; name: string } | null;
  }>;
}

export interface ReceivableRow {
  clientId: string;
  clientName: string;
  invoicesCount: number;
  totalDue: number;
  paid: number;
  remaining: number;
  oldestDueDate?: string | null;
  daysOverdue: number;
  lastReminderAt?: string | null;
  invoices: Array<{ id: string; number: string; remaining: number; dueDate?: string | null }>;
}

export interface PromiseRow {
  id: string;
  clientId: string;
  amount: Money;
  promisedDate: string;
  note?: string | null;
  status: string;
  responsible?: { id: string; name: string } | null;
}

export interface OverviewPayload {
  cards: {
    invoiced: number;
    collected: number;
    expenses: number;
    netProfit: number;
    receivables: number;
    debts: number;
    treasury: Record<string, number>;
    overdueInvoices: { count: number; value: number };
  };
  charts: {
    monthly: Array<{ month: string; in: number; out: number; profit: number }>;
    expensesByCategory: Array<{ category: string; amount: number }>;
    revenueByClient: Array<{ client: string; amount: number }>;
  };
  lastOperations: TxRow[];
  alerts: {
    overdueInvoices: number;
    unallocatedPayments: number;
    negativeAccounts: string[];
    recurringDueSoon: number;
    pendingApprovals: number;
    overdueDebts: number;
  };
}

export interface PaymentRow {
  id: string;
  reference?: string | null;
  amount: Money;
  currency: string;
  method: string;
  date: string;
  allocation: "ALLOCATED" | "UNALLOCATED" | "CANCELLED";
  note?: string | null;
  invoice?: { id: string; number: string } | null;
  payerClient?: { id: string; name: string } | null;
  account?: { id: string; name: string } | null;
}

interface Paginated<T> {
  data: T[];
  meta: { page: number; limit: number; total: number };
}

// ── API ──────────────────────────────────────────────────────────────────────

export const accountingApi = {
  overview: (params: QueryParams = {}) => apiGet<OverviewPayload>("/accounting/overview", params),

  listTransactions: (params: QueryParams = {}) =>
    apiGet<Paginated<TxRow>>("/accounting/transactions", params),
  createTransaction: (body: Record<string, unknown>) =>
    apiPost<TxRow>("/accounting/transactions", body),
  updateTransaction: (id: string, body: Record<string, unknown>) =>
    apiPatch<TxRow>(`/accounting/transactions/${id}`, body),
  approve: (id: string) => apiPost<TxRow>(`/accounting/transactions/${id}/approve`, {}),
  reject: (id: string, reason?: string) =>
    apiPost<TxRow>(`/accounting/transactions/${id}/reject`, reason ? { reason } : {}),
  requestChanges: (id: string, reason?: string) =>
    apiPost<TxRow>(`/accounting/transactions/${id}/request-changes`, reason ? { reason } : {}),
  pay: (id: string) => apiPost<TxRow>(`/accounting/transactions/${id}/pay`, {}),
  cancelTransaction: (id: string, reason?: string) =>
    apiPost<TxRow>(`/accounting/transactions/${id}/cancel`, reason ? { reason } : {}),
  duplicateTransaction: (id: string) =>
    apiPost<TxRow>(`/accounting/transactions/${id}/duplicate`, {}),
  archiveTransaction: (id: string) => apiDelete<TxRow>(`/accounting/transactions/${id}`),

  listRecurring: (params: QueryParams = {}) =>
    apiGet<Paginated<RecurringRow>>("/accounting/recurring", params),
  createRecurring: (body: Record<string, unknown>) =>
    apiPost<RecurringRow>("/accounting/recurring", body),
  updateRecurring: (id: string, body: Record<string, unknown>) =>
    apiPatch<RecurringRow>(`/accounting/recurring/${id}`, body),
  recurringAction: (
    id: string,
    action: "suspend" | "reactivate" | "stop" | "execute" | "restore",
  ) => apiPost<RecurringRow>(`/accounting/recurring/${id}/${action}`, {}),
  setRecurringNextDate: (id: string, nextExecutionAt: string) =>
    apiPost<RecurringRow>(`/accounting/recurring/${id}/next-date`, { nextExecutionAt }),
  archiveRecurring: (id: string) => apiDelete<RecurringRow>(`/accounting/recurring/${id}`),

  listDebts: (params: QueryParams = {}) =>
    apiGet<Paginated<DebtRow> & { totals: { outstanding: number } }>("/accounting/debts", params),
  createDebt: (body: Record<string, unknown>) => apiPost<DebtRow>("/accounting/debts", body),
  recordDebtPayment: (id: string, body: Record<string, unknown>) =>
    apiPost<DebtRow>(`/accounting/debts/${id}/payments`, body),
  debtAction: (id: string, action: "dispute" | "cancel", reason?: string) =>
    apiPost<DebtRow>(`/accounting/debts/${id}/${action}`, reason ? { reason } : {}),

  listReceivables: () =>
    apiGet<{
      data: ReceivableRow[];
      promises: PromiseRow[];
      totals: { remaining: number; clients: number };
    }>("/accounting/receivables"),
  sendReminder: (clientId: string, message?: string) =>
    apiPost<{ message: string; phone: string | null; email: string | null; remaining: number }>(
      "/accounting/receivables/reminder",
      { clientId, ...(message ? { message } : {}) },
    ),
  createPromise: (body: Record<string, unknown>) =>
    apiPost<PromiseRow>("/accounting/promises", body),

  report: (type: string, params: QueryParams = {}) =>
    apiGet<Record<string, unknown>>(`/accounting/reports/${type}`, params),
  listSavedReports: () =>
    apiGet<Array<{ id: string; name: string; reportType: string; createdAt: string }>>(
      "/accounting/saved-reports",
    ),
  saveReport: (body: Record<string, unknown>) =>
    apiPost<{ id: string }>("/accounting/saved-reports", body),

  listPayments: (params: QueryParams = {}) =>
    apiGet<Paginated<PaymentRow>>("/invoice-payments", params),
  recordUnallocated: (body: Record<string, unknown>) =>
    apiPost<PaymentRow>("/invoice-payments/unallocated", body),
  allocatePayment: (id: string, invoiceId: string) =>
    apiPost<unknown>(`/invoice-payments/${id}/allocate`, { invoiceId }),
  cancelPayment: (id: string, reason?: string) =>
    apiPost<PaymentRow>(`/invoice-payments/${id}/cancel`, reason ? { reason } : {}),
  /** ADR-001 — remise à zéro financière complète (OWNER/ADMIN, côté API). */
  resetFinances: () =>
    apiPost<{ purged: Record<string, number>; totalPurged: number }>("/accounting/reset", {}),
};

/**
 * Warm the Comptabilité overview cache (startup warmup — see
 * layout/useWarmupCoreData). Key must match ComptaOverviewPage's useQuery.
 */
export function prefetchAccountingOverview(qc: QueryClient): Promise<void> {
  return qc.prefetchQuery({
    queryKey: queryKeys.accountingOverview,
    queryFn: () => accountingApi.overview(),
  });
}
